import Button from "components/bootstrap/Button";
import Input from "components/bootstrap/forms/Input";
import Label from "components/bootstrap/forms/Label";
import Modal, { ModalBody, ModalFooter, ModalTitle } from "components/bootstrap/Modal";
import showNotification from "components/extras/showNotification";
import { useFormik } from "formik";
import { useMst } from "models";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import AuthService from "services/AuthService";
import { isPassword } from "lib/checker";

/******************************************************************
 * 파일명 : frontend2\src\pages\common\Headers\components\PasswordModal.js
 * 설명 : 상단 util 영역 > 비밀번호 변경 모달
 ******************************************************************/

const PasswordModal = ({
    isPasswordPopOpen,
    setIsPasswordPopOpen
}) => {
    const { t } = useTranslation();
    const {user} = useMst();
    const myEmail = user?.me?.email;
    const validate = (values) => {
        const errors = {};
        const { pwOld, pwNew, pwNew2 } = values;

        if (pwOld === '') {
            errors.pwOld = t('기존 비밀번호를 입력해주세요.');
        } else if (pwNew === '') {
            errors.pwNew = t('새 비밀번호를 입력해주세요.');
        } else if (pwNew2 === '') {
            errors.pwNew2 = t('새 비밀번호 확인을 입력해주세요.');
        } else if (pwNew !== pwNew2) {
            errors.pwNew2 = t('새 비밀번호가 일치하지 않습니다.');
        } else if (pwOld === pwNew) {
            errors.pwNew = t('기존 비밀번호와 새 비밀번호가 동일합니다.');
        }
        if (!isPassword(pwNew)) {
            errors.pwNew = t('common.validation.invalidPassword');
            errors.pwNew2 = t('common.validation.invalidPassword');
        }

        return errors;
    };

    const handleClose = () => {
        formik.resetForm(); // 초기화
        setIsPasswordPopOpen(false);
    }

    const formik = useFormik({
        initialValues: {
            pwOld: '',
            pwNew: '',
            pwNew2: '',
        },
        validate,
        validateOnBlur: false, // 타이핑 할 때 검증
        validateOnChange: false, // 다른칸으로 넘어갈 때 검증
        onSubmit: async(values) => {
            try {
                const verifyData = await AuthService.verifyPassword(values.pwOld);

                if (verifyData?.result) { // 기존 비밀번호와 일치하면
                    const updateResponse = await AuthService.changePassword(myEmail, values.pwNew);

                    if (updateResponse?.code === 200) {
                        showNotification(t('login.password'), t('비밀번호 변경이 완료되었습니다.'), 'info');
                        handleClose();
                    } else {
                        showNotification(t('login.password'), t('passwordResetFailed'), 'warning');
                    }
                } else if (verifyData?.code === 910) { // 기존 비밀번호 불일치
                    formik.setFieldTouched('pwOld', true, false);
                    formik.setFieldError('pwOld', t('기존 비밀번호가 일치하지 않습니다.'));
                } else { // 인증 만료·서버 오류 등은 불일치로 안내하지 않음
                    showNotification(t('networkError'), t('dataSaveFailed'), 'danger');
                }
            } catch (error) {
                showNotification(t('networkError'), t('dataSaveFailed'), 'danger');
            }
        },
    });

    useEffect(() => {
        if(isPasswordPopOpen){
            formik.resetForm(); // 폼 초기화
        }
        // formik 객체는 렌더링마다 새로 생성되므로 의존성에 넣으면 무한 루프 발생하기에 eslint 끄기
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPasswordPopOpen]);

    return (
        <Modal
            modalClassNameProps='pw_modal'
            classNameProps='type_basic'
            isOpen={isPasswordPopOpen}
            isCentered
            isStaticBackdrop
        >
            <ModalTitle>{t('비밀번호 변경')}</ModalTitle>
            <form noValidate onSubmit={formik.handleSubmit}>
                <ModalBody>
                    <dl>
                        <dt><Label htmlFor='pwOld'>{t('기존(임시) 비밀번호')}</Label></dt>
                        <dd>
                            <Input
                                id='pwOld'
                                type='password'
                                name='pwOld'
                                autoComplete='new-password'
                                value={formik.values.pwOld}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isValid={false}
                                isTouched={formik.touched.pwOld && !!formik.errors.pwOld} // validate에서 빈 값일 떄만 에러 표시하고 기존 비밀번호가 일치하는 지는 submit에서 검증
                                invalidFeedback={formik.errors.pwOld}
                            />
                        </dd>
                        <dt><Label htmlFor='pwNew'>{t('새 비밀번호')}</Label></dt>
                        <dd>
                            <Input
                                id='pwNew'
                                type='password'
                                name='pwNew'
                                autoComplete='new-password'
                                placeholder={t('6자 이상')}
                                value={formik.values.pwNew}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isValid={formik.isValid}
                                isTouched={formik.touched.pwNew}
                                invalidFeedback={formik.errors.pwNew}
                            />
                        </dd>
                        <dt><Label htmlFor='pwNew2'>{t('새 비밀번호 확인')}</Label></dt>
                        <dd>
                            <Input
                                id='pwNew2'
                                type='password'
                                name='pwNew2'
                                autoComplete='new-password'
                                value={formik.values.pwNew2}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isValid={formik.isValid}
                                isTouched={formik.touched.pwNew2}
                                invalidFeedback={formik.errors.pwNew2}
                            />
                        </dd>
                    </dl>
                </ModalBody>
                <ModalFooter>
                    <Button
                        type='button'
                        color='primary'
                        isOutline
                        onClick={() => {handleClose()}}
                    >
                        {t('닫기')}
                    </Button>
                    <Button
                        type='submit'
                        color='primary'
                    >
                        {t('변경')}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>

    );
};

export default PasswordModal;
